import React from 'react';
import { AlertTriangle, Calendar, MapPin, Users, Clock, Car } from 'lucide-react';

const ComponentDeleteModal = ({ component, trip, onConfirm, onCancel }) => {
  // Check if this is a chauffeur_service with an Urbanride booking
  const isChauffeuredService = component?.component_type === 'chauffeur_service';
  const componentData = component?.component_data || {};
  const urbanrideUrcn = component?.confirmation_number || componentData.urbanride_urcn || componentData.confirmation_number;
  const hasUrbanrideBooking = isChauffeuredService && urbanrideUrcn;
  const linkedComponentIds = componentData.linked_component_ids || [];
  const getComponentIcon = (type) => {
    switch (type) {
      case 'hotel':
        return '🏨';
      case 'commercial_flight':
      case 'private_flight':
        return '✈️';
      case 'meeting':
        return '🤝';
      case 'restaurant':
        return '🍽️';
      case 'rental_car':
        return '🚗';
      case 'chauffeured_service':
        return '🚘';
      case 'event':
        return '🎉';
      default:
        return '📋';
    }
  };

  const getComponentTypeLabel = (type) => {
    switch (type) {
      case 'hotel':
        return 'Hotel';
      case 'commercial_flight':
        return 'Commercial Flight';
      case 'private_flight':
        return 'Private Flight';
      case 'meeting':
        return 'Meeting';
      case 'restaurant':
        return 'Restaurant';
      case 'rental_car':
        return 'Rental Car';
      case 'chauffeured_service':
        return 'Chauffeured Service';
      case 'event':
        return 'Event';
      case 'general':
        return 'General';
      default:
        return 'Component';
    }
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return 'TBD';
    try {
      return new Date(datetime).toLocaleString();
    } catch {
      return 'TBD';
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 h-full w-full z-[70] flex items-center justify-center">
      <div className="bg-ui-surface rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-ui-red-bg rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-ui-red-fg" />
              </div>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-ui-ink">Delete Component</h3>
              <p className="text-sm text-ui-muted">This action cannot be undone</p>
            </div>
          </div>

          {/* Component Details */}
          <div className="bg-ui-sunk rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 bg-ui-line rounded-full flex items-center justify-center">
                  <span className="text-lg">{getComponentIcon(component.component_type)}</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-ui-ink truncate">
                    {component.title || 'Untitled Component'}
                  </p>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ui-blue-bg text-ui-blue-fg">
                    {getComponentTypeLabel(component.component_type)}
                  </span>
                </div>
                <div className="space-y-1">
                  {component.location && (
                    <div className="flex items-center text-xs text-ui-muted">
                      <MapPin className="w-3 h-3 mr-1" />
                      <span className="truncate">{component.location}</span>
                    </div>
                  )}
                  <div className="flex items-center text-xs text-ui-muted">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>{formatDateTime(component.start_datetime)} - {formatDateTime(component.end_datetime)}</span>
                  </div>
                  {component.confirmation_number && (
                    <div className="flex items-center text-xs text-ui-muted">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>Confirmation: {component.confirmation_number}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Urbanride Booking Warning */}
          {hasUrbanrideBooking && (
            <div className="bg-ui-blue-bg border border-ui-blue-line rounded-md p-3 mb-4">
              <div className="flex items-start">
                <Car className="w-4 h-4 text-ui-link mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-ui-link">
                  <p className="font-medium mb-1">Urbanride Booking Will Be Cancelled</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>This chauffeur service has an active Urbanride booking: <strong>{urbanrideUrcn}</strong></li>
                    <li>The booking will be cancelled via the Urbanride API</li>
                    {linkedComponentIds.length > 0 && (
                      <li>References to this booking will be removed from {linkedComponentIds.length} linked component{linkedComponentIds.length > 1 ? 's' : ''}</li>
                    )}
                    <li>Cancellation fees may apply based on the booking's cancellation policy</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-3 mb-4">
            <div className="flex items-start">
              <AlertTriangle className="w-4 h-4 text-ui-red-fg mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-ui-red-fg">
                <p className="font-medium mb-1">Warning:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>This will permanently remove this component from the trip</li>
                  <li>All associated data (reservations, notes, files) will be deleted</li>
                  <li>This action cannot be undone</li>
                  <li>The component will be removed from the timeline immediately</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Trip Context */}
          <div className="bg-ui-blue-bg border border-ui-blue-line rounded-md p-3 mb-4">
            <div className="flex items-center text-sm text-ui-link">
              <Users className="w-4 h-4 mr-2" />
              <span className="font-medium">Trip:</span>
              <span className="ml-1">{trip?.title || 'Unknown Trip'}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-ui-control rounded-md text-sm font-medium text-ui-ink-2 bg-ui-surface hover:bg-ui-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ui-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-ui-on-primary bg-ui-red-strong hover:bg-ui-red-strong/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ui-red-solid transition-colors"
            >
              {hasUrbanrideBooking ? 'Cancel Booking & Delete' : 'Delete Component'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentDeleteModal;